import { useEffect, useRef, useState } from 'react'
import Cookies from 'js-cookie'
import Api from '@/services/http'
import socket from '@/services/socket'
import store, { useSelector } from '@/store'
import { homeActions } from '@/store/homeSlice'
import { setGuideInfo } from '@/store/commonSlice'

import type { ITaskResultProps, ResponseUnReadType } from '@/store/homeSlice/type'
import { IWallInfo } from '@/store/authSlice/type'
import { updateWalletListInfo } from '@/store/authSlice'
import type { IGuideInfo } from '@/store/commonSlice/types'

/** 异步任务消息事件 */
enum MessageTypeEnum {
  /** 用户消息 */
  USER = 1,
  /** 首页通知 */
  DASHBOARD = 2,
}

/** 更新异步消息, 通知到对应业务组件 */
const updateAsyncTask = (task: ITaskResultProps) => {
  const { taskId } = task
  store.dispatch(homeActions.addTaskResultsItem(task))
  store.dispatch(homeActions.deleteTaskId({ taskId }))

  // 触发通知到页面后就自动把对应的task移除
  setTimeout(() => {
    store.dispatch(homeActions.deleteTaskResultsItem({ taskId }))
  }, 60)
}

export const updateGuideInfo = (data: IGuideInfo) => {
  store.dispatch(setGuideInfo(data))
}

/** 未读通知消息 */
export const queryUnRead = async () => {
  const states = store.getState()
  const params = {
    appId: process.env.WEBSOCKET_APP_ID,
    taskIds: states.home.taskIdCaches,
  }

  const response = await Api.admin.post<ResponseUnReadType>('/api/user/message/unread', params)
  if (response) {
    const { data, code, message } = response
    if (code === 'SUCCESS') {
      const { messageCount, exportCount, taskResults } = data
      store.dispatch(homeActions.setUnReadMessageCount({ messageCount }))
      store.dispatch(homeActions.setUnReadDownloadCount({ exportCount }))

      taskResults?.forEach((task) => {
        updateAsyncTask(task)
      })

      return data
    }

    return Promise.reject(message)
  }
}

export const updateBalance = (data: IWallInfo) => {
  store.dispatch(updateWalletListInfo(data))
}

/** socket 配置与初始化 */
export const useSocket = () => {
  const isInit = useRef(false)

  // 用户消息
  useEffect(() => {
    return socket.onUserMessage((payload) => {
      // 后端没法保证用户消息都有messageType字段，兼容设置默认值1
      const { messageCount, userMessage, messageType = 1 } = payload
      switch (messageType) {
        case MessageTypeEnum.USER:
          store.dispatch(homeActions.setUnReadMessageCount({ messageCount }))
          store.dispatch(homeActions.setSocketMessageList([userMessage]))
        case MessageTypeEnum.DASHBOARD:
          store.dispatch(homeActions.setHasNotifyCount(true))
          break
        default:
          break
      }
    })
  }, [])

  // 导出任务消息
  useEffect(() => {
    return socket.onExportListMessage((payload) => {
      const { exportCount, exportMessage } = payload
      store.dispatch(homeActions.setUnReadDownloadCount({ exportCount }))
      store.dispatch(homeActions.setSocketDownloadList([exportMessage]))
    })
  }, [])

  // 异步任务消息
  useEffect(() => {
    return socket.onAsyncResult(updateAsyncTask)
  }, [])

  // 断开连接，重新获取未读消息
  useEffect(() => {
    return socket.onClosed(queryUnRead)
  }, [])

  // 监听余额更新
  useEffect(() => {
    return socket.onBalanceUpdate(updateBalance)
  }, [])

  // 监听新手指引更新
  useEffect(() => {
    return socket.onGuideInfoUpdate(updateGuideInfo)
  }, [])

  // 获取未读信息
  useEffect(() => {
    queryUnRead()
  }, [])

  useEffect(() => {
    const udbSubAppId = Cookies.get('osudb_subappid')
    const uid = Cookies.get('osudb_uid')
    if (uid && udbSubAppId && !isInit.current) {
      isInit.current = true

      // 配置
      socket.configure(() => ({
        env: process.env.APP_ENV.toUpperCase(),
        /**
         * @todo
         * 此处根据打包环境，不能根据用户真正所在地区进行就近 websocket 连接
         * 后面可以根据服务器环境确认 socket 连接地址
         */
        wsDomain: window.ONE_WS_ENV === 'nation' ? process.env.WEBSOCKET_URL_NATION : process.env.WEBSOCKET_URL,
        appId: process.env.WEBSOCKET_APP_ID,
        privateKey: process.env.WEBSOCKET_PRIVATE_KEY,
        broadcasts: typeof process.env.WEBSOCKET_UDB_BOARDCASTS === 'string' ? process.env.WEBSOCKET_UDB_BOARDCASTS.split(',') : [],
        udbAuthUrl: `${window.location.origin}/udb/lgn/sdk/getCookieOtp.do`,
        udbAppId: process.env.WEBSOCKET_UDB_APP_ID,
        udbSubAppId,
        uid,
      }))

      socket.open()
    }

    return () => {
      if (isInit.current) {
        socket?.close()
      }
    }
  }, [])
}

/** socket 版本更新 */
export const useVersionUpdate = () => {
  const [needUpdate, setNeedUpdate] = useState(false)
  const myuid = useSelector((state) => state.auth.osudb_uid)

  useEffect(() => {
    return socket.onVersionUpdate((payload) => {
      const { version, uid } = payload
      const users = typeof uid === 'string' && uid.length > 0 ? uid.split(',') : []

      // 如果用户为0 或者 UID 为空字符串均当做所有用户启用
      if (users.length > 0) {
        if (users.includes(myuid) && version !== process.env.GIT_COMMIT_HASH) {
          setNeedUpdate(true)
        }
      } else if (version !== process.env.GIT_COMMIT_HASH) {
        setNeedUpdate(true)
      }
    })
  }, [myuid])

  return needUpdate
}
